import {useEffect, useState, useRef} from 'react';
import {Link, useOutlet} from 'react-router-dom';
import {useDispatch} from 'react-redux';

import {addMicroservice, clearMicroservices} from '../../features/microservices.slice';
import Context from './Context';
import _axios from '../../utils/axios';
import {ADMIN, DEFAULT_GROUP} from '../../utils/settings';
import validate from '../../utils/validator';
import {handleError} from '../../utils/utils';

import styles from '../../styles/base_components/Navbar.module.scss';
import _kc from "../../utils/keycloak";
import styled from "styled-components";

const Content = styled.div`
  &&& {
    ${props => props.displayContent && 'display: block;'}
  }
`;

function Navbar(props) {
  const [groups, setGroups] = useState({});
  const [users, setUsers] = useState({});

  const [dropdownItems, setDropdownItems] = useState([]);
  const [displayContent, setDisplayContent] = useState(false);
  const outlet = useOutlet();
  const dispatch = useDispatch();

  const ref = useRef();

  function clickDropdownItem(item) {
    return event => {
      switch (item) {
        case 'ADMIN':
          window.location.href = '/admin';
          break;
        case 'GROUP ADMIN':
          window.location.href = '/group-admin';
          break;
        case 'USER':
          window.location.href = '/';
          break;
        default:
          _kc.logout();
          break;
      }
    };
  }

  function onClickContent(event) {
    setDisplayContent(displayContent => !displayContent);
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setDisplayContent(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    dispatch(clearMicroservices());

    _axios.get(ADMIN.MICROSERVICES_PATH)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The microservices from the database do not correspond to our standard.');
        if (ok !== 1) throw new Error('The database path to microservices is currently unavailable.');
        if (data.length === 0) return;

        let error = '';
        for (let microservice of data) {
          let err = validate([
            {value: microservice.id, type: 'db_microservice_id'},
            {value: microservice.fetchers, type: 'db_microservice_fetchers'},
            {value: microservice.status, type: 'db_microservice_status'},
            {value: microservice.host, type: 'db_microservice_host'},
            {value: microservice.uri, type: 'db_microservice_uri'},
            {value: microservice.version, type: 'db_microservice_version'},
            {value: microservice.groups, type: 'db_microservice_groups'},
            {value: microservice.author, type: 'db_microservice_author'},
            {value: microservice.name, type: 'db_microservice_name'},
            {value: microservice.created_at, type: 'db_microservice_CreatedAt'},
            {value: microservice.updated_at, type: 'db_microservice_UpdatedAt'}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + microservice.host + ': ' + err.join('. ') + '\n';
          else dispatch(addMicroservice(microservice));
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
      })
      .catch(handleError);
  }, [dispatch]);

  useEffect(() => {
    const roles = _kc.tokenParsed.realm_access.roles.filter(role => role.search(/_admin/) !== -1);
    let di = [];
    if (roles.find(role => role === 'guests_admin')) di.push('ADMIN');
    if (roles.find(role => role !== 'guests_admin')) di.push('GROUP ADMIN');
    di.push('USER');
    di.push('LOGOUT');
    setDropdownItems(di);

    _axios.get(ADMIN.USERS_PATH)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The users from the database do not correspond to our standard');
        if (ok !== 1) throw new Error('The database path to users is currently unavailable.');
        if (data.length === 0) return;

        let error = '';
        for (let user of data) {
          let err = validate([
            {value: user.id, type: 'db_user_id'},
            {value: user.username, type: 'db_user_username'},
            // {value: user.email, type: 'db_user_email'},
            {value: user.groups, type: 'db_user_groups'},
            {value: user.roles, type: 'db_user_roles'},
            {value: user.enabled, type: 'db_user_enabled', empty: true}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + user.username + ' ' + user.email + ': ' + err.join('. ') + '\n';
          else setUsers(users => {
            let newUser = {};
            newUser[user.id] = {
              username: user.username,
              email: user.email,
              groups: user.groups,
              roles: user.roles,
              enabled: user.enabled
            };
            return Object.assign(newUser, users);
          });
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
      })
      .catch(handleError);
    _axios.get(ADMIN.GROUPS_PATH)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The microservices from the database do not correspond to our standard.');
        if (ok !== 1) throw new Error('The database path to microservices is currently unavailable.');
        if (data.length === 0) return;

        let hasDefaultGroup = false;
        let error = '';
        let g = {};
        for (let group of data) {
          let err = validate([
            {value: group.id, type: 'db_group_id'},
            {value: group.name, type: 'db_group_name'},
            {value: group.path, type: 'db_group_path'},
            {value: group.subGroups, type: 'db_group_subgroups'}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + group.name + ': ' + err.join('. ') + '\n';
          else setGroups(groups => {
            let newGroup = {};
            newGroup[group.name] = {
              id: group.id,
              path: group.path,
              subGroups: group.subGroups
            };
            return Object.assign(newGroup, groups);
          });

          if (group.name === DEFAULT_GROUP) hasDefaultGroup = true;
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
        if (!hasDefaultGroup) throw new Error('The default group ' + DEFAULT_GROUP + ' is missing from the database');
      })
      .catch(handleError);
  }, []);

  return (
    <Context.Provider value={{
      users: users,
      setUsers: setUsers,
      groups: groups,
      setGroups: setGroups,
      role: ADMIN,
      defaultGroup: DEFAULT_GROUP
    }}>
      <div className={styles.navbar}>
        <Link to='/admin/microservices'>microservices</Link>
        <Link to='/admin'>users</Link>

        <div className={styles.dropdown} ref={ref} onClick={onClickContent}>
          <div/>
          <Content className={styles.content} displayContent={displayContent}>
            {dropdownItems.map(item => (
              <p key={item} onClick={clickDropdownItem(item)}>{item}</p>
            ))}
          </Content>
        </div>
      </div>

      {outlet}
    </Context.Provider>
  );
}

export default Navbar;